import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useViewMode, type ViewMode } from "@/hooks/useViewMode";
import { useAuth } from "@/hooks/useAuth";

/**
 * Automatically switches the active view mode based on the current route,
 * so the navbar/UI matches what the user is actually looking at.
 *
 * Mapping:
 * - Attendee ("explore"): /, /events, /event/:slug, /my-tickets, /scan, /notifications, /profile
 * - Organizer:            /manage, /manage/:id, /create-event, /check-in/:id, /scan-tickets, /event/:slug/edit
 *
 * Notes:
 * - Routes not listed (auth pages, /backlog, /promoter/manage, /promoter-scan, etc.) leave viewMode untouched.
 * - We only switch INTO modes the user is allowed to use.
 */
const getModeForPath = (pathname: string): ViewMode | null => {
  if (
    pathname.startsWith("/manage/") ||
    pathname === "/manage" ||
    pathname === "/create-event" ||
    pathname.startsWith("/check-in/") ||
    pathname === "/scan-tickets" ||
    /^\/event\/[^/]+\/edit$/.test(pathname)
  ) {
    return "organizer";
  }

  // Attendee-facing routes do NOT force a downgrade to "explore". If the
  // user is in organizer mode and visits one of these routes
  // (e.g. /events to browse), we leave their chosen mode alone so the
  // navbar toggle persists.
  return null;
};

const userHasMode = (
  mode: ViewMode,
  profile: ReturnType<typeof useAuth>["profile"],
): boolean => {
  if (mode === "explore") return true;
  if (!profile) return false;

  if (mode === "organizer") {
    return (profile.professionalRoles || []).some(
      (r) =>
        (r.roleType === "organizer" || r.roleType === "event_organizer") &&
        (r.approvalStatus === "approved" || r.approvalStatus === "pending"),
    );
  }

  return false;
};

export const ViewModeRouteSync = () => {
  const location = useLocation();
  const { viewMode, setViewMode } = useViewMode();
  const { user, profile, isLoading } = useAuth();
  const lastSyncedPathRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading || !user) return;

    if (lastSyncedPathRef.current === location.pathname) return;
    lastSyncedPathRef.current = location.pathname;

    const targetMode = getModeForPath(location.pathname);
    if (!targetMode || targetMode === viewMode) return;

    if (userHasMode(targetMode, profile)) {
      setViewMode(targetMode);
    }
  }, [location.pathname, viewMode, setViewMode, user, profile, isLoading]);

  return null;
};
